import { useCallback, useEffect, useMemo, useState } from 'react';

import { SORT_OPTION_VALUES } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import {
  QUERY_PARAMS_RESERVED,
  VIEW_MODES,
  ROUTE_DATE_FORMATS,
  PAGE_CATEGORIES,
  ROUTE_RESERVED_KEYWORDS,
  FILTER_TYPES,
} from 'operabase-router/constants';

const useSelectedViewMode = ({ filters, forcedViewMode }) => {
  const { isDesktop } = useDeviceTypeLayouts();
  const [state, setState] = useState({
    viewMode: isDesktop ? VIEW_MODES.SEASON : VIEW_MODES.DAY,
    sortBy: SORT_OPTION_VALUES.YEAR_ASC,
    isReady: false,
  });
  const { entityType, entity, baseRoute } = usePageContext();
  const { obRouteContext } = useAppContext();
  const isRentalsListing =
    obRouteContext?.linkProps?.filters[FILTER_TYPES.RENTALS] ||
    obRouteContext?.page?.tab === ROUTE_RESERVED_KEYWORDS.rentals;
  const { category, tab, stats, isListingProductions } = obRouteContext?.page || {};
  const isEntityStubPageOverview = useMemo(() => category === PAGE_CATEGORIES.STUB && !tab, [tab, category]);
  const today = createDate();
  const isCastingProfile = baseRoute === ROUTE_RESERVED_KEYWORDS.casting && !!entity?.id;

  const {
    hasDateFilterApplied,
    hasLargeArchiveDateRange,
    hasUpcomingFilterApplied,
    hasWatchOnlineFilterApplied,
    hasArchivedFilterApplied,
    hasRentalFilterApplied,
    hasMoreThanYearRangeFilterApplied,
    featuredProductionFilterParams,
    selectedViewMode,
    hasFutureDates,
  } = useMemo(() => {
    const { date_from, date_to, has_online = false, has_active_rental = false } = filters || {};

    return {
      hasDateFilterApplied: !!(date_from || date_to),
      hasLargeArchiveDateRange:
        (!!date_from && !!date_to && createDate(date_to).diff(createDate(date_from).subtract(1, 'day'), 'year') >= 1) ||
        (!date_from &&
          !!date_to &&
          (createDate(date_to).isBefore(today, 'day') || createDate(date_to).isSame(today, 'day'))),
      hasUpcomingFilterApplied: !date_to && !!date_from && createDate(date_from).isSame(today, 'day'),
      hasWatchOnlineFilterApplied: has_online,
      hasArchivedFilterApplied: !!date_to && createDate(date_to).isBefore(today, 'day'),
      hasRentalFilterApplied: has_active_rental,
      hasFutureDates:
        (!!date_from && (createDate(date_from).isAfter(today, 'day') || createDate(date_from).isSame(today, 'day'))) ||
        (!!date_to && createDate(date_to).isAfter(today, 'day')),
      hasMoreThanYearRangeFilterApplied:
        !!date_from && !!date_to && createDate(date_to).diff(createDate(date_from).subtract(1, 'day'), 'year') >= 1,
      featuredProductionFilterParams: {
        date_to: today.format(ROUTE_DATE_FORMATS.STANDARD),
      },
      selectedViewMode: forcedViewMode || obRouteContext?.query[QUERY_PARAMS_RESERVED.VIEW_MODE],
    };
  }, [today, filters, forcedViewMode, obRouteContext?.query]);

  const queryFilters = useMemo(() => {
    const {
      enable_ml: _enableML,
      has_media: _hasMedia,
      mediaLimit: _mediaLimit,
      is_cancelled: _isCancelled,
      sort: _sort,
      page: _page,
      include_virtual_contributors: _includeVirtualContributors,
      ...restFilters
    } = filters || {};

    const dateFrom =
      restFilters?.date_from &&
      (createDate(restFilters?.date_from).isAfter(today, 'day') ||
        createDate(restFilters?.date_from).isSame(today, 'day'))
        ? restFilters?.date_from
        : today.format(ROUTE_DATE_FORMATS.STANDARD);
    const dateTo =
      restFilters?.date_to &&
      (createDate(restFilters?.date_to).isAfter(dateFrom, 'day') ||
        createDate(restFilters?.date_to).isSame(dateFrom, 'day'))
        ? restFilters?.date_to
        : null;

    return {
      ...(entityType && { entityType }),
      ...(entity?.id && { entityId: entity?.id }),
      filters: {
        ...restFilters,
        date_from: dateFrom,
        date_to: dateTo,
      },
      limit: 1,
      queryConfig: {
        select: response => response?.total,
        enabled: !selectedViewMode && (hasFutureDates || isEntityStubPageOverview),
        ...(stats?.filters?.upcoming >= 0 && {
          initialData: {
            total: stats?.filters?.upcoming,
          },
        }),
      },
    };
  }, [entityType, entity, filters, hasFutureDates, isEntityStubPageOverview, stats, today, selectedViewMode]);

  const { data: upcomingProductionCount, isFetching } = useQuery(queries.getProductionCount(queryFilters));
  const hasMoreThanThreeUpcomingProductions = upcomingProductionCount > 3;
  const isApiDone = queryFilters?.queryConfig?.enabled ? !isFetching : true;

  const getViewModeSortBy = useCallback(() => {
    if (isRentalsListing) {
      return {
        viewMode: VIEW_MODES.GRID,
        sortBy: SORT_OPTION_VALUES.FEATURED_DESC,
        isReady: true,
      };
    }

    if (isCastingProfile && !selectedViewMode) {
      return {
        viewMode: VIEW_MODES.DAY,
        sortBy: SORT_OPTION_VALUES.YEAR_ASC,
        isReady: true,
      };
    }

    if (selectedViewMode) {
      if (selectedViewMode === VIEW_MODES.MINI && isDesktop) {
        return {
          viewMode: VIEW_MODES.CALENDAR,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      if (selectedViewMode === VIEW_MODES.CALENDAR && !isDesktop) {
        return {
          viewMode: VIEW_MODES.MINI,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      if (Object.values(VIEW_MODES).includes(selectedViewMode)) {
        return {
          viewMode: selectedViewMode,
          sortBy: selectedViewMode === VIEW_MODES.GRID ? SORT_OPTION_VALUES.FEATURED_DESC : SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }
    }

    const selectedSortBy = obRouteContext?.query[QUERY_PARAMS_RESERVED.SORT_BY];

    if (SORT_OPTION_VALUES.FEATURED_DESC === selectedSortBy) {
      return {
        viewMode: VIEW_MODES.GRID,
        sortBy: SORT_OPTION_VALUES.FEATURED_DESC,
        isReady: true,
      };
    }

    const isSortedByDate = selectedSortBy === SORT_OPTION_VALUES.YEAR_ASC;

    if (isDesktop) {
      if (isEntityStubPageOverview) {
        if (hasMoreThanThreeUpcomingProductions) {
          return {
            viewMode: VIEW_MODES.CALENDAR,
            sortBy: SORT_OPTION_VALUES.YEAR_ASC,
            isReady: true,
          };
        }

        return {
          viewMode: VIEW_MODES.DAY,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      if (hasFutureDates) {
        if (hasMoreThanThreeUpcomingProductions || hasMoreThanYearRangeFilterApplied) {
          return {
            viewMode: VIEW_MODES.CALENDAR,
            sortBy: SORT_OPTION_VALUES.YEAR_ASC,
            isReady: true,
          };
        }

        return {
          viewMode: VIEW_MODES.DAY,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      if (hasMoreThanYearRangeFilterApplied && hasFutureDates) {
        return {
          viewMode: VIEW_MODES.CALENDAR,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      if (
        !isSortedByDate &&
        (!hasDateFilterApplied ||
          hasWatchOnlineFilterApplied ||
          hasArchivedFilterApplied ||
          hasRentalFilterApplied ||
          hasLargeArchiveDateRange)
      ) {
        return {
          viewMode: VIEW_MODES.GRID,
          sortBy: SORT_OPTION_VALUES.FEATURED_DESC,
          isReady: true,
        };
      }

      return {
        viewMode: VIEW_MODES.SEASON,
        sortBy: SORT_OPTION_VALUES.YEAR_ASC,
        isReady: true,
      };
    }

    if (isEntityStubPageOverview) {
      if (hasMoreThanThreeUpcomingProductions) {
        return {
          viewMode: VIEW_MODES.MINI,
          sortBy: SORT_OPTION_VALUES.YEAR_ASC,
          isReady: true,
        };
      }

      return {
        viewMode: VIEW_MODES.DAY,
        sortBy: SORT_OPTION_VALUES.YEAR_ASC,
        isReady: true,
      };
    }

    if (hasWatchOnlineFilterApplied || hasRentalFilterApplied) {
      return {
        viewMode: VIEW_MODES.GRID,
        sortBy: SORT_OPTION_VALUES.FEATURED_DESC,
        isReady: true,
      };
    }

    return {
      viewMode: VIEW_MODES.MINI,
      sortBy: SORT_OPTION_VALUES.YEAR_ASC,
      isReady: true,
    };
  }, [
    obRouteContext?.query,
    isDesktop,
    hasMoreThanThreeUpcomingProductions,
    hasMoreThanYearRangeFilterApplied,
    hasFutureDates,
    hasDateFilterApplied,
    hasWatchOnlineFilterApplied,
    hasArchivedFilterApplied,
    hasRentalFilterApplied,
    isEntityStubPageOverview,
    hasLargeArchiveDateRange,
    selectedViewMode,
    isCastingProfile,
    isRentalsListing,
  ]);

  useEffect(() => {
    if (isApiDone) {
      setState(getViewModeSortBy());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApiDone, isDesktop, obRouteContext?.query]);

  const setViewMode = useCallback(viewMode => {
    setState(prevState => ({
      ...prevState,
      viewMode,
    }));
  }, []);

  const setSortBy = useCallback(sortBy => {
    setState(prevState => ({
      ...prevState,
      sortBy,
    }));
  }, []);

  const showFeaturedPastProductions = useMemo(
    () => hasUpcomingFilterApplied && !isEntityStubPageOverview && isListingProductions && !isCastingProfile,
    [hasUpcomingFilterApplied, isEntityStubPageOverview, isListingProductions, isCastingProfile],
  );

  return {
    ...state,
    showFeaturedPastProductions,
    featuredProductionFilterParams,
    setViewMode,
    setSortBy,
  };
};

export default useSelectedViewMode;
